import type { CalculationLegend } from "../3d";
import type { LockCalculatorDefinition } from "../../../../http/lock_calculator/types";
import { getName } from "../../../../utils";
import { formatCoefficient } from "./coefficientFormat";
import { effectiveGammaMAluminium } from "./form";
import { coefficientDiffers } from "./notices";
import { hasRealLoadValue } from "./structuralVisibility";

/** Already-translated labels, read from the same translations the form steps and the notices use. */
export type CalculationLegendLabels = {
  standard: string;
  loadDurationClass: string;
  kmod: string;
  gammaM: string;
  gammaMAluminium: string;
  /** Marks a coefficient that is not the normative value. */
  custom: string;
  headerMaterial: string;
  joistMaterial: string;
  combined: string;
};

type LegendSource = Pick<
  LockCalculatorDefinition,
  | "standard"
  | "loadDurationClass"
  | "kmod"
  | "kmodDefault"
  | "gammaMConnection"
  | "gammaMConnectionDefault"
  | "gammaMAluminium"
  | "gammaMAluminiumDefault"
  | "headerMaterialGrade"
  | "joistMaterialGrade"
  | "verticalLoad"
  | "upliftLoad"
  | "axialLoad"
  | "lateralLoad"
  | "includeFv"
  | "includeFup"
  | "includeFax"
  | "includeFlat"
>;

/** The combined verification needs at least this many axes with a load, as in the backend (minCombinedAxes). */
const MIN_COMBINED_AXES = 2;

/**
 * The 3D legend rows on the calculation settings: standard, load duration class, the coefficients in force
 * (kmod, then γM aluminium, then γM connection), the timber materials and the combined verification. A
 * coefficient that differs from the normative value is flagged, as in the PDF report. A row whose data the
 * backend has not resolved yet is left out.
 */
export const buildCalculationLegend = (
  calculation: LegendSource | null | undefined,
  labels: CalculationLegendLabels,
  locale: string,
): CalculationLegend => {
  const coefficient = (label: string, value: number | null | undefined, normative: number | null | undefined) =>
    value == null
      ? []
      : [`${label}: ${formatCoefficient(value)}${coefficientDiffers(value, normative) ? ` (${labels.custom})` : ""}`];
  const material = (label: string, grade: { code: string } | null | undefined) =>
    grade ? [`${label}: ${grade.code}`] : [];
  const combinedAxes = [
    { included: calculation?.includeFv, load: calculation?.verticalLoad, symbol: "Fv,d" },
    { included: calculation?.includeFup, load: calculation?.upliftLoad, symbol: "Fup,d" },
    { included: calculation?.includeFax, load: calculation?.axialLoad, symbol: "Fax,d" },
    { included: calculation?.includeFlat, load: calculation?.lateralLoad, symbol: "Flat,d" },
  ].filter((axis) => axis.included && hasRealLoadValue(axis.load));
  return {
    standard: calculation?.standard ? `${labels.standard}: ${calculation.standard.code}` : null,
    loadDuration: calculation?.loadDurationClass
      ? `${labels.loadDurationClass}: ${getName(calculation.loadDurationClass, locale)}`
      : null,
    coefficients: [
      ...coefficient(labels.kmod, calculation?.kmod, calculation?.kmodDefault),
      ...coefficient(
        labels.gammaMAluminium,
        calculation ? effectiveGammaMAluminium(calculation) : null,
        calculation?.gammaMAluminiumDefault,
      ),
      ...coefficient(labels.gammaM, calculation?.gammaMConnection, calculation?.gammaMConnectionDefault),
    ],
    timber: [
      ...material(labels.headerMaterial, calculation?.headerMaterialGrade),
      ...material(labels.joistMaterial, calculation?.joistMaterialGrade),
    ],
    combined:
      combinedAxes.length >= MIN_COMBINED_AXES
        ? `${labels.combined}: ${combinedAxes.map((axis) => axis.symbol).join(", ")}`
        : null,
  };
};
